import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { AlertTriangle, User, MessageCircle, Calendar } from 'lucide-react';

export default function AdminViolationsPage() {
  const [violations, setViolations] = useState([]);
  const [platformHealth, setPlatformHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [violationsRes, healthRes] = await Promise.all([
        api.getViolations(),
        api.getPlatformHealth()
      ]);
      setViolations(violationsRes.data);
      setPlatformHealth(healthRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-navy-800 mb-6 sm:mb-8">Business Protection Dashboard</h1>

        {/* Platform Health */}
        {platformHealth && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-navy-800">
                    {platformHealth.platform_metrics.conversion_rate}%
                  </div>
                  <div className="text-xs sm:text-sm text-slate-800 mt-1">Platform Conversion Rate</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-red-600">
                    {platformHealth.platform_metrics.total_violations}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-800 mt-1">Total Violations</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-orange-600">
                    {platformHealth.platform_metrics.high_risk_clients}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-800 mt-1">High Risk Clients</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Violations List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Contact Sharing Violations ({violations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {violations.length === 0 ? (
              <div className="text-center py-8 text-slate-800">
                No violations detected. Platform is secure!
              </div>
            ) : (
              <div className="space-y-4">
                {violations.map((violation) => (
                  <Card key={violation.id} className="border-2 border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-red-600" />
                          <div>
                            <div className="font-semibold text-red-900">
                              {violation.vendor_name}
                            </div>
                            <div className="text-sm text-red-700">
                              {violation.vendor_email}
                            </div>
                          </div>
                        </div>
                        <Badge className="bg-red-600 text-white">
                          {violation.vendor_violation_count} violations
                        </Badge>
                      </div>
                      
                      <div className="bg-white p-3 rounded border border-red-300 mb-3">
                        <div className="text-sm font-medium text-red-800 mb-1">
                          Blocked Message:
                        </div>
                        <div className="text-sm text-red-900">
                          "{violation.blocked_message}"
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-red-700">
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {violation.reason}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(violation.created_at).toLocaleString()}
                        </span>
                      </div>

                      {violation.vendor_violation_count >= 3 && (
                        <div className="mt-3 p-2 bg-red-100 rounded border border-red-400">
                          <p className="text-sm text-red-900 font-semibold">
                            ⚠️ VENDOR SUSPENDED - 3 Strike Rule Applied
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
